import { setShowSuccess, setCouponCode, clearCouponCodes, setPresaleCode, setIsPresaleValid, setIsPresaleApplied } from 'checkout/views/coupon/actions/coupon';
import { setEventTimes, setSelectedEvent, setEventsToday, setFetchedEventsToday, setSelectedEventTimeObject, setChildEventAdditionalInfo } from '../views/series/actions/event-series';
import { clearSelectedPriceLevels } from '../views/ticket-picker/actions/ticket-picker';
import { clearSelectedBundles } from '../views/bundle-picker/actions/bundle-picker';
import { clearSelectedProducts } from '../views/product-picker/actions/product-picker';
import { removeListingItem } from '../../listing/helpers/storage';
import { reservationActive } from '../selectors/reservation';
import { isValidDateObject, ISOStringToDate } from '../../helpers/time';
import { serializeToUrl } from 'checkout/helpers/helpers';
import { setShouldFetchUpsells } from 'checkout/views/upsells/actions/upsells';
import { setLoading } from './loading';
import { wrappedFetch } from '../helpers/fetch';
import { setHideBestAvailable } from './ui';

export function setEventData(data) {
    return { type: "FETCH_EVENT", data }
}

export function setFetchedEventId(id) {
    return { type: "SET_FETCHED_EVENT", id };
}

export function refreshEventData(data) {
    return { type: "REFRESH_EVENT", data }
}

export function setDataForEventIfNotSet(eventId, eventData, subReducerKey = null) {
    return { type: "SET_DATA_FOR_EVENT_IF_NOT_SET", eventId, eventData, subReducerKey };
}

export function setPriceLevels(data, subReducerKey = null) {
    return { type: "SET_PRICE_LEVELS", data, subReducerKey }
}

export function setEventPriceLevels(id, data) {
  return {
    type: "SET_EVENT_PRICE_LEVELES",
    data,
    id
  }
}

export function updatePriceLevels(data, subReducerKey = null) {
    return { type: "UPDATE_PRICE_LEVELS", data, subReducerKey }
}
export function setBundles(data, subReducerKey = null) {
    return { type: "SET_BUNDLES", data, subReducerKey }
}

export function setProducts(data, subReducerKey = null) {
    return { type: "SET_PRODUCTS", data, subReducerKey }
}

export function setChildEvent(data, subReducerKey = null) {
    return { type: "SET_CHILD_EVENT", data, subReducerKey }
}

export function setEventRefreshLoading(value) {
    return { type: "SET_EVENT_REFRESH_LOADING", value }
}

export function fetchEvent() {
    return (dispatch, getState) => {
        let state = getState();
        let now = new Date();
        let timestamp = Math.floor(now.getTime() / 1000);
        dispatch(setEventRefreshLoading(true));
        let url = `${state.ui.apiUrl}/listing-settings/${state.ui.listingId}/listing-data?timestamp=${timestamp}`;
        return wrappedFetch(url).then((res) => {
            return res.json();
        }).then(result => {
            if (result && result.attributes && result.attributes.listing_data) {
                //refresh state
                let state = getState();
                //do not update if checkout is being actively used
                if (!reservationActive(state) && !state.ui.overlay) {
                    dispatch(refreshEventData(result.attributes.listing_data));
                    if (result.attributes.series && result.attributes.series.events_today) {
                        dispatch(setFetchedEventsToday(true));
                        if (Array.isArray(result.attributes.series.events_today)) {
                            let now = new Date();
                            dispatch(setEventsToday(result.attributes.series.events_today.map(event => {
                                //may be behind on marking as post_sale due to caching, so check against salesClose time here
                                let salesCloseTime = ISOStringToDate(event.salesClose);
                                if (isValidDateObject(salesCloseTime) && salesCloseTime.getTime() < now.getTime()) {
                                    return {
                                        ...event,
                                        eventStatus: "post_sale",
                                    };
                                }
                                return event;
                            })));
                        } else {
                            dispatch(setEventsToday([]));
                        }
                    }
                }
            }
        }).catch(err => {
            console.log(err);
        }).finally(() => {
            dispatch(setEventRefreshLoading(false));
        });
    }
}

async function loadInfoData (requests, useCouponService, fallBackUrl) {
    const responses = await Promise.allSettled(requests);
    if (useCouponService && responses[1]?.status !== 'fulfilled') {
        return await Promise.allSettled([wrappedFetch(fallBackUrl)]);
    }
    return responses;
}

export function fetchExtraData(eventId) {
    return (dispatch, getState) => {
        const state = getState();
        const slug = state.ui.listingSlug;

        const urls = [
            (slug && state.ui.listingId) ? `${state.ui.apiUrl}/listings/${slug}/${eventId}/info` : `${state.ui.apiUrl}/listings/${eventId}/cms-info`,
        ];

        // check if we are using coupon service
        const useCouponService = !!state.ui.couponApiUrl;
        if (useCouponService) {
            urls.push(`${state.ui.couponApiUrl}/events/${eventId}/coupons/exists`);
        }

        dispatch(setLoading("FETCH_EXTRA_EVENT_DATA_REQUEST"));
        const requests = urls.map(url => wrappedFetch(url));
        loadInfoData(
            requests,
            useCouponService,
            `${state.ui.apiUrl}/listings/${slug}/${eventId}/info?force=true`
        ).then(responses => {
            responses[0].value.json().then((data) => {
                dispatch({
                    type: "FETCH_LISTING_INFO", data
                });

                if (Object.keys(data?.event || [])?.length !== 0) {
                    const newEvent = {...data.event};
                    if (useCouponService) {
                        const { value } = responses[1] || {};
                        // If the status is fulfilled, then a value is present. If the status is rejected, then a reason is present
                        if (value) {
                            value.json().then((data) => {
                                const { data: responseData } = data;
                                newEvent.attributes.events[eventId].event.hasCoupons = responseData?.hasCoupons;
                            });
                        }
                    }
                    dispatch(setEventData(newEvent));
                }

                // eslint-disable-next-line no-extra-boolean-cast
                if (!!data?.event?.attributes?.shouldFetchUpsells) {
                    dispatch(setShouldFetchUpsells(true));
                }

                const hideBestAvailable = data?.event?.attributes?.events?.[eventId]?.reserved_seating?.hideBestAvailable;

                dispatch(setHideBestAvailable(!!hideBestAvailable));

                dispatch(setLoading("FETCH_EXTRA_EVENT_DATA_SUCCESS"));
            });
        })
    }
}

export function getEventPriceLevels() {
    return (dispatch, getState) => {
        let state = getState();
        let payload = {
            listingId: state.ui.listingId,
            eventId: Object.values(state.event.events)[0].event.event_id,
        };

        return wrappedFetch(state.ui.apiUrl + '/price-levels-event?' + serializeToUrl(payload), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        }).then((response) => {
            return response.json();
        }).then((data) => {
            dispatch(setPriceLevels(data.price_levels));
        }).catch(() => { });
    }
}

export function clearChildEventDetails(reducerKey = null) {
    return (dispatch, getState) => {
        const state = getState();
        dispatch(setEventTimes([], null, reducerKey));
        dispatch(setChildEvent({}, reducerKey));
        dispatch(setSelectedEvent({}, reducerKey));
        dispatch(setChildEventAdditionalInfo(''));
        if (!reducerKey) {
            removeListingItem(state.ui.listingSlug, 'selectedEventId');
            dispatch(setShowSuccess(false));
            dispatch(setCouponCode(""));
            dispatch(clearCouponCodes());
        }

        dispatch(clearSelectedPriceLevels(reducerKey));
        dispatch(clearSelectedBundles(reducerKey));
        dispatch(clearSelectedProducts(reducerKey));
        if (!reducerKey) {
            dispatch(setPresaleCode(""));
            dispatch(setIsPresaleValid(false));
            dispatch(setIsPresaleApplied(false));
        }
        dispatch(setSelectedEventTimeObject(null, reducerKey));
    }
}
